import { getErrorMessage, logMessage } from '../utils/helpers';
import { Zlib } from '../js/zlib';
import { remoteConfigGetter } from '../utils/remoteConfig';
import { HandleSocket } from '../components/common/handleSocket';
import ReconnectingWebSocket from 'reconnecting-websocket';
import Queue from 'queue';
import Subscribed from '../models/symbolData';
import { ResolveSymbolInfo, Quotes } from '../models/symbolData';
import { LastDailyBar, OdinMessagePacket, Handlers } from '../models/charts';
import { LastBarsCache, HistoryTailCache, lastBarKey } from './bar-cache';
// Vendored from mobile_charts — see scripts/sync-vendored.sh there.
// @ts-ignore -- plain-JS vendored module, no .d.ts (allowJs is on)
import { compileSession, isInSession, istParts, sessionPhase } from './vendor/session-timeline';
import { MarketDepth, Bids } from '../models/future';
import { FeedUiController } from '../components/TVChartContainer/title_sync';

// ---- session guard (CAS) ---------------------------------------------------
// Compiled session specs, keyed by the session string so every handler sharing a
// session shares one parse.
const sessionSpecCache = new Map<string, any>();
function specFor(symbol: ResolveSymbolInfo): any {
    if (!symbol || !symbol.session) return null;
    const key = `${symbol.session}|${symbol.session_holidays || ''}|${symbol.corrections || ''}`;
    let spec = sessionSpecCache.get(key);
    if (spec === undefined) {
        spec = compileSession(symbol.session, symbol.session_holidays, symbol.corrections);
        if (!spec.ok) logMessage(`streaming: session compile failed (${spec.code}) for ${symbol.session}`);
        sessionSpecCache.set(key, spec);
    }
    return spec;
}
// If a handler starts dropping everything, the session data is likelier wrong
// than the market — fail open rather than let a chart go silent.
//
// A count alone is NOT a safe trigger. Verified live 2026-07-30 against real
// pre-prod ticks: a liquid scrip produces ~1 tick/second, so 50 consecutive
// out-of-session ticks accumulate inside a minute. On a CAS day the auction
// runs 15:15-15:30 and would blow through 50 immediately — the guard would
// switch itself off around 15:16 EVERY day and then let auction prices write
// the daily close, which is the exact corruption it exists to prevent. (1-min
// charts survive that because TV drops out-of-session bar TIMES itself; 1D/1W/1M
// do not, since the bar's timestamp is the day, which is in session.)
//
// So give up only when the drops also SPAN longer than any legitimate
// out-of-session window: the auction is 15 minutes and pre-open is 15 minutes,
// while genuinely wrong session data drops ticks for the rest of the day. The
// span is measured on tick time, not wall clock, so it can't be skewed by a
// slow client. The streak resets on each new trading day so one bad afternoon
// cannot leave a long-lived terminal tab unguarded tomorrow morning.
const OUT_OF_SESSION_GIVE_UP = 50;
const OUT_OF_SESSION_GIVE_UP_MS = 45 * 60 * 1000;
function passesSessionGuard(handler: any, tradeTime: number): boolean {
    const spec = handler.sessionSpec;
    if (!spec || !spec.ok) return true;                       // fail-open
    const ymd = istParts(tradeTime).ymd;
    if (handler.__oosYmd !== ymd) {                           // new trading day: re-arm
        handler.__oosYmd = ymd;
        handler.__oosStreak = 0;
        handler.__oosFirstAt = null;
        handler.__sessionGuardOff = false;
    }
    if (handler.__sessionGuardOff) return true;
    if (typeof window !== 'undefined' && (window as any).__casGuardOff) return true;
    if (isInSession(spec, tradeTime)) {
        handler.__oosStreak = 0;
        handler.__oosFirstAt = null;
        return true;
    }
    handler.__oosStreak = (handler.__oosStreak || 0) + 1;
    if (handler.__oosFirstAt == null) handler.__oosFirstAt = tradeTime;
    if (typeof window !== 'undefined') {
        (window as any).__tvOutOfSessionTicks = ((window as any).__tvOutOfSessionTicks || 0) + 1;
    }
    const spanMs = tradeTime - handler.__oosFirstAt;
    if (handler.__oosStreak >= OUT_OF_SESSION_GIVE_UP && spanMs >= OUT_OF_SESSION_GIVE_UP_MS) {
        handler.__sessionGuardOff = true;
        logMessage(`streaming: session guard disabled for ${handler.symbol} after ${handler.__oosStreak} drops spanning ${spanMs}ms`);
    }
    return false;
}

// ---- pre/post-market marker ------------------------------------------------
// The mirror image of the guard above: a tick the guard drops is a tick the
// candle cannot show, which is exactly the tick worth surfacing on the price
// scale instead. TradingView draws that marker (a dotted line plus a
// price-scale label) from the QUOTE stream — but the two fields belong to
// different owners:
//
//   `rtc` (+ rch/rchp/rtc_time) is OURS. TV never writes it; the label shows
//   whatever we last sent.
//
//   `current_session` is TV'S. It computes the phase itself, once a second,
//   from the symbol's session, and merges the result over anything we send —
//   so our value survives about a second and is only a seed for the first
//   paint. TV can only ever produce pre/post-market for a symbol that declares
//   `subsessions`; see subsessionsFor() in vendor/session-timeline.js, which is
//   what actually makes the marker appear.
//
// So the phase computed here has one job that matters: deciding whether to send
// `rtc` at all. Requires the `pre_post_market_price_line` featureset. Kept
// deliberately in step with mobile_charts/lib/streaming_provider.js — see
// PRE-POST-MARKET.md there for the captures this is built on.
export interface ExtendedQuoteFields {
    current_session: string;
    rtc?: number;
    rtc_time?: number;
    rch?: number;
    rchp?: number;
}

// Is ODIN tag 418 an auction price on this tick, or something else entirely?
//
// It is not a dedicated field. Measured across the 2026-08-12 captures, when
// 418 is non-zero it is byte-identical to tag 88 (open interest) on every
// option and future, and to the index level on NIFTY/BANKNIFTY. Rendering
// either as a price gives labels like "7496" on an option trading at 24.40.
//
// Tag 417 (the auction reference price) is the discriminator: across every
// instrument captured, 417 is present exactly on those where 418 is a genuine
// auction price — HEROMOTOCO (417=5883.00, 418=5895.00) and RELIANCE/BSE
// (417=1321.05, 418 live 1325.00 -> 1327.10) — and absent wherever 418 is OI
// or an index level. The proximity band is belt-and-braces if that stops
// holding: an auction price is a price for the SAME instrument.
const AUCTION_PRICE_MAX_DEVIATION = 0.2;   // 20%
function usableAuctionPrice(iep?: number, refPrice?: number, ltp?: number): number {
    const p = Number(iep);
    if (!(p > 0)) return 0;
    if (!(Number(refPrice) > 0)) return 0;          // not an auction-bearing instrument
    if (ltp && ltp > 0 && Math.abs(p - ltp) / ltp > AUCTION_PRICE_MAX_DEVIATION) return 0;
    return p;
}

/**
 * Build the extended-hours quote fields for one tick. All prices already divided.
 *
 * Unlike mobile_charts — which receives a pre-parsed payload that carries
 * neither tag — this surface parses the raw packet itself, so the LIVE
 * indicative price is available here from day one. During CAS the LTP stays
 * frozen at the last continuous trade until the auction prints, while 418
 * tracks the auction and converges on the close.
 *
 * `phaseAtMs` is WHEN WE ARE; `tradeTimeMs` is when the price happened. Two
 * different questions that must not share an answer — the phase describes the
 * market now, while `rtc_time` stamps the price itself.
 *
 * Judging the phase by the tick's own timestamp is what broke this on
 * 2026-08-12: after the close the feed heartbeats with a stale (and sometimes
 * timezone-skewed) last-update time, which produced `out_of_session` and — since
 * TV merges quote updates — poisoned an otherwise correct quote. The socket path
 * therefore passes Date.now(). The REST path passes its own Date.now() as
 * tradeTimeMs (a snapshot carries no tick time), so the default suits it.
 */
export function extendedQuoteFields(
    spec: any, tradeTimeMs: number, ltp: number, prevClose: number,
    iep?: number, refPrice?: number, phaseAtMs?: number
): ExtendedQuoteFields {
    const phase: string = sessionPhase(spec, phaseAtMs === undefined ? tradeTimeMs : phaseAtMs);
    const out: ExtendedQuoteFields = { current_session: phase };
    if (phase !== 'pre_market' && phase !== 'post_market') return out;
    let price = usableAuctionPrice(iep, refPrice, ltp);
    // In pre-open the LTP *is* the indicative price, and once CAS prints the
    // LTP *is* the closing price — so LTP is a correct fallback, not a guess.
    if (!(price > 0)) price = ltp;
    if (!(price > 0)) return out;
    out.rtc = price;
    out.rtc_time = Math.floor(tradeTimeMs / 1000);
    if (prevClose > 0) {
        out.rch = price - prevClose;
        out.rchp = (price - prevClose) / prevClose * 100;
    }
    return out;
}

/**
 * REST-snapshot variant — for `getQuotes`, which has no handler and therefore no
 * authoritative session. Deliberately NARROWER than extendedQuoteFields(), in
 * two ways that are both bug fixes:
 *
 * 1. It never sends `current_session`. The symbolInfo reaching here comes from
 *    `quoteSymbolMap`, filled either from `_quoteInfoFromSeed` (identity only —
 *    NO session) or from a plain resolve that has not had the per-symbol CAS
 *    override applied (so it carries the SEGMENT DEFAULT, 0915-1530). Either way
 *    its answer can disagree with the chart's, and a wrong 'market' overwrites a
 *    correct 'post_market' and makes the marker VANISH. That is the "line goes
 *    away on window/viewport switch" report — a switch makes TV re-quote, and
 *    with no session sessionPhase fails closed to 'market', while with the
 *    segment default it says 'market' right through 15:15-15:30, i.e. exactly
 *    during the auction. TV computes the phase itself from the CHART symbol's
 *    subsessions, so leaving it alone is strictly better than seeding it from a
 *    source that may be wrong.
 *
 * 2. It sends `rtc` only when the price is provably from outside regular hours.
 *    A REST snapshot carries no auction tags (the /aux/analytics/quote payload
 *    has no iep or reference_price at all), so `ltp` is the last CONTINUOUS
 *    trade — after a closing auction, exactly what the last candle already shows.
 *    Verified 2026-08-13 on HEROMOTOCO: the snapshot read last_trade_price=5820
 *    stamped last_trade_time=15:14:59 while the auction had settled at 5850, a
 *    number that exists ONLY in tag 418. Drawing 5820 as "Post" duplicated the
 *    candle, and because this path re-runs on every re-quote it also overwrote
 *    the correct socket-derived 5850 — the "badge snaps back to the candle
 *    close" report.
 *
 * `lastTradeTimeMs` is the snapshot's own last_trade_time (epoch ms). Without it
 * we cannot prove the price is extended-hours, so we send nothing.
 */
export function extendedQuoteFieldsForSymbol(
    symbolInfo: ResolveSymbolInfo, tradeTimeMs: number, ltp: number, prevClose: number,
    iep?: number, refPrice?: number, lastTradeTimeMs?: number
): Partial<ExtendedQuoteFields> {
    const spec = specFor(symbolInfo);
    if (!spec || !spec.ok) return {};
    const phase: string = sessionPhase(spec, tradeTimeMs);
    if (phase !== 'pre_market' && phase !== 'post_market') return {};
    // A genuine auction price needs no alibi; anything else must prove itself.
    if (!(usableAuctionPrice(iep, refPrice, ltp) > 0)) {
        if (lastTradeTimeMs === undefined) return {};
        if (sessionPhase(spec, lastTradeTimeMs) === 'market') return {};
    }
    const out: Partial<ExtendedQuoteFields> =
        extendedQuoteFields(spec, tradeTimeMs, ltp, prevClose, iep, refPrice);
    delete out.current_session;      // see (1) — the phase is not ours to assert here
    return out;
}

const moment = require('moment');

let worker: Worker | undefined;
const channelToSubscription = new Map<string, OdinMessagePacket>();
const singleQuoteMap = new Map();
// uid -> array of channelStrings. A bar subscription on an index with a volume
// proxy spans TWO channels (the index spot channel + the nearest-future channel),
// so a single guid can map to more than one channel — hence an array (ADVA-847).
const guidToSubscription = new Map();
// Index volume/OI proxy (ADVA-847, ported from mobile_charts ADVA-846/ADVA-1100).
// All proxy accounting lives ON the volume-proxy HANDLER, not in channel-global
// maps:
//   handler.proxyLatest = { cumVolume, perCandle, bucket, oi, oi_ch }
//     — what the uid-paired index 'bar' handler reads instead of its own spot
//       tick (which carries volume=0 / no OI).
//   handler.proxySt = { bucket, base, lastCum }
//     — the running candle aggregation behind `perCandle` at THIS
//       subscription's resolution.
// Per-handler (ADVA-1100 switch fix): TradingView overlaps the new bar
// subscription with the old one on a timeframe/symbol switch, and both ride the
// same future channelString. Channel-global state survived the switch (the
// one-shot history seed never re-applied → forming candle lost its history
// volume) and the two handlers' different bucket granularities re-based each
// other every tick (→ ~zero live volume until refresh). State scoped to the
// handler starts fresh per subscription, dies with it in unsubscribe, and lets
// two charts legitimately share one future channel.

const queue = Queue({ results: [] });
queue.autostart = true;
queue.timeout = 1000;
queue.concurrency = 1;

// ----- reconnect backfill + gap-bridge ----------------------------------
// After tab inactivity / network loss, the socket reconnects but the outage
// window shows flat candles. On reconnect we refetch /history to backfill the
// gap; while that lands we hold ticks (pendingReload) so they resume on the
// real backfilled bar, and if the refetch never lands we synthesize a current-
// period bar so the chart resumes instead of freezing. Mirrors mobile_charts.
const PENDING_RELOAD_TIMEOUT_MS = 7000;
const RELOAD_THROTTLE_MS = 3000;
const VISIBILITY_RELOAD_THRESHOLD_MS = 10000;   // hidden ≥ this → backfill on return
const FORCE_RECONNECT_THRESHOLD_MS = 60000;     // hidden ≥ this → also force a fresh socket
// First socket_opened is the initial connect (TV already loaded history); every
// later open is a post-outage reconnect that must backfill the missed candles.
let hasConnectedBefore = false;
let lastReloadAt = 0;
let hiddenSince = 0;
// TV's onResetCacheNeededCallback per bar series, keyed by subscriber UID
// (mirrors mobile_charts).
//
// Symbol keying — what this used to do — is wrong on a multiple-chart layout:
// the same scrip can back several series at once (two charts on it, or two
// resolutions of it) and only the last one's callback survived, so every other
// chart's cache was never invalidated after a gap.
//
// The earlier attempt at UID keying emptied the map to 0 after the first reset
// because the delete lived INSIDE the async `queue` while the set is
// synchronous, so a resubscribe that reused the UID was wiped by the queued
// delete that ran after it. The delete is now synchronous, at the top of
// unsubscribe(), which restores the correct ordering.
//
// Just as importantly, the backfill no longer DEPENDS on this map: reloadChartBars
// invalidates TV's cache wholesale via widget.resetCache() and forces the refetch
// with resetData() on every chart. So even a transiently empty map still backfills.
//   uid -> { symbol, cb }
const resetCacheCallbackMap = new Map<string, { symbol: string; cb: () => void }>();

// Synthesize a current-period bar to resume from when the /history refetch never
// lands. Period start matches the bar-builder's per-resolution calc (IST offset)
// so the next tick lands in-period and accumulates correctly.
function synthesizePostTimeoutBar(resolution: string, tradeTimeMs: number, tradePrice: number, cumulativeVolume: number): LastDailyBar {
    let periodStart: number;
    if (resolution == '1') {
        periodStart = moment(tradeTimeMs).startOf('minute').toDate().getTime();
    } else if (resolution == '1S') {
        periodStart = moment(tradeTimeMs).startOf('second').toDate().getTime();
    } else if (resolution == '1D') {
        periodStart = moment(tradeTimeMs).startOf('day').toDate().getTime() + 19800000;
    } else if (resolution == '1W') {
        periodStart = moment(tradeTimeMs).startOf('week').toDate().getTime() + 86400000 + 19800000;
    } else if (resolution == '1M') {
        periodStart = moment(tradeTimeMs).startOf('month').toDate().getTime() + 19800000;
    } else if (resolution == '1Y') {
        periodStart = moment(tradeTimeMs).startOf('year').toDate().getTime() + 19800000;
    } else {
        periodStart = moment(tradeTimeMs).startOf('minute').toDate().getTime();
    }
    const safePrice = Number.isFinite(tradePrice) ? tradePrice : 0;
    const safeCumulative = Number.isFinite(cumulativeVolume) ? cumulativeVolume : 0;
    return { time: periodStart, open: safePrice, high: safePrice, low: safePrice, close: safePrice, volume: 0, totalLastDayVolumeTillBar: safeCumulative };
}

// Kick the worker to reconnect a possibly-zombie socket (network back / tab resumed).
function forceReconnect() {
    worker?.postMessage({ type: 'force_reconnect' });
}

function onOnline() {
    logMessage('reconnect: online → backfill + force reconnect');
    reloadChartBars('online');
    forceReconnect();
}

function onVisibilityChange() {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'hidden') {
        hiddenSince = Date.now();
        return;
    }
    if (!hiddenSince) return;
    const hiddenMs = Date.now() - hiddenSince;
    hiddenSince = 0;
    if (hiddenMs > VISIBILITY_RELOAD_THRESHOLD_MS) {
        logMessage(`reconnect: tab visible after ${Math.round(hiddenMs / 1000)}s → backfill`);
        reloadChartBars('visibility');
        if (hiddenMs > FORCE_RECONNECT_THRESHOLD_MS) forceReconnect();
    }
}

// Faithful port of mobile_charts ResetAllBarsCache: drop the last-bar cache, mark
// every bar handler for re-seed (pendingReload), then FIRE every series'
// onResetCacheNeededCallback so TV re-requests /history and backfills the gap.
// The callback firing is the actual refetch trigger — this is what was missing.
export function resetAllBarsCache() {
    LastBarsCache.clear();
    for (const subscriptionItem of channelToSubscription.values()) {
        if (!subscriptionItem || !subscriptionItem.handlers) continue;
        subscriptionItem.handlers.forEach((handler: Handlers) => {
            if (handler.type === 'bar') {
                handler.pendingReload = true;
                handler.pendingReloadSince = Date.now();
            }
        });
    }
    resetCacheCallbackMap.forEach((entry) => {
        try { entry.cb(); } catch (e) { logMessage('reconnect: reset callback failed ' + getErrorMessage(e), 2); }
    });
}

// Backfill candles missed during an outage. Mirrors mobile_charts reloadChartBars:
// resetAllBarsCache() (fires every series' onResetCacheNeededCallback → TV
// re-requests /history) AND resetData() on EVERY chart — both, every time.
//
// resetData() is per-chart and is the only thing that actually triggers the
// refetch: firing the datafeed's onResetCacheNeededCallback alone does NOT
// (verified against TT v32). So the old activeChart()-only call healed the
// focused chart and left every other chart in a multiple-chart layout showing
// the gap until it was touched.
//
// `force` skips the throttle for deliberate, non-bursty triggers (a layout
// switch revealing charts that were hidden through the outage).
export function reloadChartBars(source: string, force = false) {
    const now = Date.now();
    if (!force && now - lastReloadAt < RELOAD_THROTTLE_MS) { return; }
    lastReloadAt = now;
    resetAllBarsCache();
    let charts = 0;
    try {
        const w: any = (typeof window !== 'undefined') ? (window as any).tvWidget : undefined;
        if (w && typeof w.chartsCount === 'function') {
            // Invalidate TV's own bar cache for every symbol/resolution in one
            // call (v32+), so the backfill doesn't depend on resetCacheCallbackMap
            // being complete at this instant.
            try { if (typeof w.resetCache === 'function') w.resetCache(); } catch (e) { /* best-effort */ }
            charts = w.chartsCount();
            for (let i = 0; i < charts; i++) {
                // Per-chart try: one chart throwing must not skip the rest.
                try { w.chart(i).resetData(); } catch (e) { logMessage(`reconnect: resetData failed for chart ${i} ` + getErrorMessage(e), 2); }
            }
        } else if (w && typeof w.activeChart === 'function' && w.activeChart()) {
            w.activeChart().resetData();
            charts = 1;
        }
    } catch (e) { logMessage('reconnect: resetData failed ' + getErrorMessage(e), 2); }
    logMessage(`reconnect: backfill(${source}) → ${resetCacheCallbackMap.size} series, ${charts} charts`);
}
// ------------------------------------------------------------------------

// Recreate the subscription map
function recreateMap() {
    for (const [channelString, subscriptionItem] of channelToSubscription.entries()) {
        const [market_segment_id, token] = channelString.split('-');
        if (subscriptionItem.isSubscribed) {
            let payload: ResolveSymbolInfo = {
                market_segment_id: Number(market_segment_id),
                token: token
            }
            if (subscriptionItem.isMarketDepth) {
                SendMessage(
                    _getSubscribeDepthRequest(payload)
                );
            } else {
                SendMessage(
                    _getSubscribeTouchRequest(payload)
                );
            }
        }
    }
}

// Check if the browser is Chrome
function isChrome(): boolean {
    return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
}

let socketUrl: string;

// Open WebSocket connection
export function openWebSocketConnection(ocToken: string, clientCode: string) {
    if (window.Worker) {
        if (worker) {
            worker.terminate();
        }

        socketUrl = remoteConfigGetter('ltp_socket_url').asString();

        worker = new Worker(`${process.env.PUBLIC_URL}/worker.js`);
        worker.postMessage({ type: 'initialize', socketUrl, ocToken, clientCode, });
        FeedUiController.setStatus('CONNECTING');
        worker.onmessage = (e: MessageEvent) => {
            if (e.data.type === 'socket_opened') {
                console.log('Worker socket opened');
                HandleSocket(true);
                recreateMap();
                // First open = initial connect (TV already has history). Every later
                // open is a reconnect after an outage — backfill the missed candles.
                if (hasConnectedBefore) {
                    logMessage('reconnect: socket reopened → backfill');
                    reloadChartBars('socket_reopen');
                } else {
                    hasConnectedBefore = true;
                }
            } else if (e.data.type === 'socket_closed') {
                HandleSocket(false);
            } else if (e.data.type === 'socket_error') {
                console.error('Worker socket error in socket_error:', e.data);
            } else if (e.data.type === 'tick') {
                ProcessPacketString(e.data.data);
            } else if (e.data.type === 'websocket_state') {
                console.log('WebSocket state changed:', e.data.state);
                FeedUiController.setStatus(e.data.state);
            } else if (e.data.type === 'warn') {
                console.warn('Worker warning:', e.data.message);
            }
        };

        worker.onmessageerror = (e: MessageEvent) => {
            console.trace('Worker error', e);
        };

        // Recover a zombie socket promptly when the OS network returns or a
        // long-frozen tab resumes (onclose may never fire, so it won't reconnect
        // on its own). The chart backfills missed candles separately, on the
        // resulting socket_opened.
        window.removeEventListener('online', onOnline);
        window.addEventListener('online', onOnline);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        document.addEventListener('visibilitychange', onVisibilityChange);
    }
}

// Close WebSocket connection
export function closeConnection() {
    logMessage('Streaming Provider: Close connection called');
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    hasConnectedBefore = false;
    if (worker) {
        worker.postMessage({ type: 'close' });
        worker.terminate();
        worker = undefined;
    }
    channelToSubscription.clear();
    singleQuoteMap.clear();
    guidToSubscription.clear();
}

// Temporarily close WebSocket connection
export function closeConnectionTemporarily() {
    logMessage('Streaming Provider: Closing connection temporarily');
    if (worker) {
        worker.postMessage({ type: 'temporary_close' });
    }
}

// Subscribe to Odin stream
export function subscribeOnOdinStream(
    symbols: ResolveSymbolInfo[],
    resolution: string | undefined,
    onRealtimeCallback: (a: any) => void,
    subscribeUID: string,
    onResetCacheNeededCallback: () => void | undefined,
    lastDailyBar: LastDailyBar | undefined,
    type: string
) {
    // console.log("lastDailyBar",lastDailyBar)
    // Store TV's "refetch now" callback per SERIES (see resetCacheCallbackMap) so
    // every chart in a multiple-chart layout gets its cache invalidated on a
    // reconnect, not just whichever one subscribed last.
    if (type === 'bar' && typeof onResetCacheNeededCallback === 'function') {
        symbols.forEach((s) => resetCacheCallbackMap.set(subscribeUID, { symbol: String(s.symbol), cb: onResetCacheNeededCallback }));
    }
    queue.push((cb) => {
        try {
            if (type === 'depth') {
                _subscribeDepth(symbols[0], onRealtimeCallback, subscribeUID);
            } else if (type === 'quotes' || type === 'tt-quotes') {
                _subscribeQuotes(symbols, onRealtimeCallback, subscribeUID, type);
            } else if (type === 'bar') {
                _subscribeBars(
                    symbols,
                    onRealtimeCallback,
                    subscribeUID,
                    lastDailyBar as LastDailyBar,
                    String(resolution)
                );
            } else if (type === 'single-quotes') {
                _subscribeSingleQuotes(
                    symbols,
                    onRealtimeCallback,
                    subscribeUID
                );
            }

            symbols.forEach((symbol: ResolveSymbolInfo) => {
                const channelString: string = `${symbol.odin_market_segment_id}-${symbol.odin_token}`;
                const subs: OdinMessagePacket = channelToSubscription.get(channelString) as OdinMessagePacket;
                if (subs) {
                    let needsDepth: boolean = false;
                    let needsTouchline: boolean = false;

                    subs.handlers.forEach((handler: Handlers) => {
                        if (handler.type === 'depth') {
                            needsDepth = true;
                        } else {
                            needsTouchline = true;
                        }
                    });

                    if (!subs.isSubscribed) {
                        if (needsDepth) {
                            const request = _getSubscribeDepthRequest(symbol);
                            SendMessage(request);
                            subs.isSubscribed = true;
                            subs.isMarketDepth = true;
                        } else {
                            const request = _getSubscribeTouchRequest(symbol);
                            SendMessage(request);
                            subs.isSubscribed = true;
                            subs.isTouchline = true;
                        }
                        channelToSubscription.set(channelString, subs);
                    } else {
                        if (needsDepth && !subs.isMarketDepth) {
                            let request = _getUnsubscribeTouchRequest(symbol);
                            SendMessage(request);

                            request = _getSubscribeDepthRequest(symbol);
                            SendMessage(request);

                            subs.isTouchline = false;
                            subs.isMarketDepth = true;
                            channelToSubscription.set(channelString, subs);
                        }
                        ProcessPacketString(subs.lastPacket);
                    }
                }
            });
        } catch (e) {
            console.log('Subscription error', e);
        }
        if (cb) {
            cb();
        }
    });
}


export function unsubscribe(listenerGuid: string) {
    // Drop this series' reset callback SYNCHRONOUSLY — deliberately outside the
    // queue below. TV unsubscribes before it resubscribes, so doing this inline
    // keeps a reused UID's fresh entry intact, whereas a queued delete would run
    // after the resubscribe and wipe it (see resetCacheCallbackMap).
    resetCacheCallbackMap.delete(listenerGuid);
    queue.push((cb) => {
        let channels = guidToSubscription.get(listenerGuid);
        if (!channels) {
            if (cb) {
                cb();
            }
            return
        }
        // A guid can own more than one channel (index bar + its volume proxy), so
        // iterate. Older entries may still be a bare string — normalise both.
        const channelList = Array.isArray(channels) ? channels : [channels];
        channelList.forEach((channelString: any) => {
            const [market_segment_id, token] = channelString.split("-")
            let subscriptionItem = channelToSubscription.get(channelString)
            if (!subscriptionItem) {
                return
            }
            subscriptionItem.handlers = subscriptionItem.handlers.filter(function (handler: Handlers) {
                return handler.id != listenerGuid;
            })
            channelToSubscription.set(channelString, subscriptionItem)
            if (subscriptionItem.handlers) {
                if (subscriptionItem.handlers.length == 0) {
                    if (subscriptionItem["isSubscribed"]) {
                        if (subscriptionItem["isMarketDepth"]) {
                            var request = _getSubscribeDepthRequest({ odin_market_segment_id: market_segment_id, odin_token: token })
                            SendMessage(request)
                        } else {
                            var request = _getUnsubscribeTouchRequest({ odin_market_segment_id: market_segment_id, odin_token: token })
                            SendMessage(request)
                        }
                    }
                    channelToSubscription.delete(channelString)
                    // Proxy volume/OI state lives on the handlers themselves
                    // (handler.proxySt / handler.proxyLatest) and dies with them.
                } else {
                    var needsDepth = false
                    subscriptionItem.handlers.forEach((handler: Handlers) => {
                        if (handler.type == 'depth') {
                            needsDepth = true
                        }
                    })
                    if (!needsDepth) {
                        if (subscriptionItem["isMarketDepth"]) {
                            var request2 = _getUnsubscribeDepthRequest({ odin_market_segment_id: market_segment_id, odin_token: token })
                            SendMessage(request2)
                            request2 = _getSubscribeTouchRequest({ odin_market_segment_id: market_segment_id, odin_token: token })
                            SendMessage(request2)
                            subscriptionItem["isSubscribed"] = true
                            subscriptionItem["isTouchline"] = true
                            subscriptionItem["isMarketDepth"] = false
                            channelToSubscription.set(channelString, subscriptionItem)
                        }
                    }
                }
            }
        })
        guidToSubscription.delete(listenerGuid)
        if (cb) {
            cb();
        }
    })

}

//Varibales name change done
function SendMessage(odinMessage: string) {
    if (worker != null) {
        worker.postMessage({ type: 'message', message: odinMessage });
    }
}


// Exported for the ADVA-847 real-module tests (drives ticks the way the worker
// does in production); not part of the public datafeed surface.
export function ProcessPacketString(data: string) {
    let data_map: Map<string, string> = marketDepthProcessMessage(data);

    let message_code = parseInt(String(data_map.get('64')));
    if (message_code != 128 && message_code != 209) {
        return
    }

    let tradePrice = parseFloat(String(data_map.get('8')));
    let openPrice = parseFloat(String(data_map.get('75')));
    let highPrice = parseFloat(String(data_map.get('77')));
    let lowPrice = parseFloat(String(data_map.get('78')));
    let closePrice = parseFloat(String(data_map.get('76')));
    let volume = parseFloat(String(data_map.get('79')));
    let totalBuyQty = data_map.get('81');
    let totalSellQty = data_map.get('82');
    let divider = parseFloat(String(data_map.get('399')) || '100')
    let token = data_map.get('7')
    let market_segment = data_map.get('1')
    let lastTradeQuantity = parseFloat(String(data_map.get('9')));
    let dpr = data_map.get('380');
    let oi = data_map.get('88');
    let oi_ch = data_map.get('465');
    // Call-auction tags. Only sent during an auction, and before an equilibrium
    // is struck they arrive as 0, so both stay undefined rather than reaching
    // consumers as NaN or a 0.00 price. 417 = auction reference price,
    // 418 = indicative equilibrium price — see usableAuctionPrice for why 418
    // is never trusted without 417 beside it.
    const casPrice = (tag: string): number | undefined => {
        const raw = parseFloat(String(data_map.get(tag)));
        return Number.isFinite(raw) && raw > 0 ? raw / divider : undefined;
    };
    const referencePrice = casPrice('417');
    const iep = casPrice('418');
    let depth: MarketDepth = data_map.get('depth') as MarketDepth
    // let buy_qty = parseFloat(  depth.asks[0]?.volume ||data_map.get('2') || "0");
    // let buy_price = 0.0
    let buy_qty: number, buy_price: number, sell_qty: number, sell_price: number = 0.0
    if (data_map.has('3') && data_map.has('5')) {
        buy_price = parseFloat(String(data_map.get('3'))) / divider
        sell_price = parseFloat(String(data_map.get('6'))) / divider
        buy_qty = parseFloat(String(data_map.get('2')))
        sell_qty = parseFloat(String(data_map.get('5')))
    } else {
        depth.asks?.forEach((row: Bids, index: number) => {
            if (depth.asks) {
                depth.asks[index].price = depth.asks[index].price / divider
            }
        })
        depth.bids?.forEach((row: Bids, index: number) => {
            if (depth.bids)
                depth.bids[index].price = depth.bids[index].price / divider
        })
        if (depth.bids && depth.asks) {
            buy_qty = depth.asks[0]?.volume
            sell_qty = depth.asks[0]?.volume
            buy_price = depth.bids[4]?.price
            sell_price = depth.asks[0]?.price
        }
    }
    var tradeTime = odinDateParse(data_map.get('74') as string);

    let channelString = market_segment + "-" + token;

    let subscriptionItem = channelToSubscription.get(channelString);
    if (!subscriptionItem) {
        return;
    }
    if (!subscriptionItem.handlers) {
        return;
    }
    subscriptionItem.lastPacket = data
    channelToSubscription.set(channelString, subscriptionItem);
    // console.log(subscriptionItem)
    subscriptionItem.handlers.forEach((handler: Handlers) => {
        // Pre/post-market marker. Per handler because the session is per handler
        // — a volume-proxy handler rides the future's channel but carries the
        // INDEX's session (see makeHandler), so one channel can legitimately
        // hold handlers sitting in two different phases.
        //
        // Computed ONLY for the two handlers that consume it. This is the
        // hottest path in the app (every tick x every handler) and the other
        // four types — bar, volume-proxy, single-quotes, depth — would allocate
        // a Date and an object per tick only to discard them.
        //
        // Phase from the wall clock, NOT tradeTime — after the close the feed
        // heartbeats with a stale/skewed last-update time, and a wrong phase here
        // overwrites a good one in TV's merged quote. See extendedQuoteFields.
        const isQuoteHandler = handler.type === 'quotes' || handler.type === 'tt-quotes';
        const extended = isQuoteHandler ? extendedQuoteFields(
            (handler as any).sessionSpec, tradeTime, tradePrice / divider,
            closePrice > 0 ? closePrice / divider : 0, iep, referencePrice,
            Date.now()) : null;
        if (handler.type == 'quotes') {
            let quote = {
                s: 'ok',
                n: handler.symbol,
                v: {
                    ch: (tradePrice - closePrice) / divider,
                    chp: ((tradePrice - closePrice) / closePrice * 100),
                    short_name: handler.symbol.split(":")[1],
                    exchange: handler.symbol.split(":")[0],
                    description: handler.symbol,
                    lp: tradePrice / divider,
                    open_price: openPrice / divider,
                    high_price: highPrice / divider,
                    low_price: lowPrice / divider,
                    prev_close_price: closePrice / divider,
                    volume: volume,
                    dpr: dpr,
                    token: token,
                    market_segment_id: market_segment,
                    oi: oi,
                    oi_ch: oi_ch,
                    ask: sell_price,
                    ask_qty: sell_qty,
                    bid: buy_price,
                    bid_qty: buy_qty,
                    average_trade_price: ((highPrice + lowPrice) / 2) / (divider),
                    reference_price: referencePrice,
                    iep: iep,
                    ...extended
                }

            }
            try {
                handler.handler.callback([quote])
            }
            catch (err) {
                console.info(err)
            }

        } else if (handler.type == 'tt-quotes') {
            let quote = {
                s: 'success',
                n: handler.symbol,
                v: {
                    ch: (tradePrice - closePrice) / divider,
                    chp: ((tradePrice - closePrice) / closePrice * 100),
                    short_name: handler.symbol.split(":")[1],
                    exchange: handler.symbol.split(":")[0],
                    description: handler.symbol,
                    lp: tradePrice / divider,
                    open_price: openPrice / divider,
                    high_price: highPrice / divider,
                    low_price: lowPrice / divider,
                    prev_close_price: closePrice / divider,
                    volume: volume,
                    dpr: dpr,
                    ask: sell_price,
                    ask_qty: sell_qty,
                    bid: buy_price,
                    bid_qty: buy_qty,
                    average_trade_price: ((highPrice + lowPrice) / 2) / (divider),
                    reference_price: referencePrice,
                    iep: iep,
                    ...extended
                }

            }
            try {
                handler.handler.callback(quote)
            }
            catch (err) {
                console.info(err)
            }
        } else if (handler.type == 'bar') {
            // CAS: drop ticks outside this symbol's session for this date.
            // Intraday this is mostly defence-in-depth (TV discards out-of-session
            // realtime bars itself). It MUST exist for 1D/1W/1M: those bars are
            // stamped with the whole-day bucket — always a valid bar time — and
            // `close` comes from the tick unconditionally, so an indicative
            // equilibrium tick during the 15:15-15:30 auction would overwrite the
            // visible daily close with a price that never traded. W/M/Y high/low
            // are running max/min in OUR state, so one excursion sticks until a
            // history refetch. Also removes pre-open and post-close ticks.
            if (!passesSessionGuard(handler, tradeTime)) return;
            // After a reconnect (resetAllBarsCache set pendingReload): hold this
            // handler's ticks until the fresh /history lands, then reseed
            // lastDailyBar from LastBarsCache so ticks resume on the backfilled
            // history instead of the stale pre-gap bar. If the refetch never lands
            // within the timeout, synthesize a current-period bar so the chart
            // resumes instead of freezing on flat lines.
            if (handler.pendingReload) {
                // Keyed on THIS handler's own resolution, so a sibling chart
                // showing the same symbol on another timeframe can't seed us
                // its bar (see bar-cache.ts).
                const fresh = handler.fullName ? LastBarsCache.get(lastBarKey(handler.fullName, handler.resolution)) : undefined;
                if (fresh !== undefined) {
                    handler.lastDailyBar = fresh;
                    // Re-anchor live volume accounting to the fresh history
                    // (ADVA-1100) — the next tick re-seeds from `fresh`. For a
                    // proxied index, re-anchor the paired future handler too so
                    // the forming candle re-seeds from the refetched bar.
                    handler.histSeedBar = fresh;
                    handler.liveVolState = undefined;
                    const pf = pairedProxyHandler(handler);
                    if (pf) {
                        pf.histSeedBar = fresh;
                        pf.proxySt = undefined;
                    }
                    handler.pendingReload = false;
                } else if (handler.pendingReloadSince && Date.now() - handler.pendingReloadSince > PENDING_RELOAD_TIMEOUT_MS) {
                    handler.lastDailyBar = synthesizePostTimeoutBar(handler.resolution, tradeTime, tradePrice / divider, volume);
                    // Anchor accounting to the synthetic bar (volume 0 at the
                    // current period) so the recovery bar grows from the
                    // recovery point, per the synthesize contract (ADVA-1100).
                    handler.histSeedBar = handler.lastDailyBar;
                    handler.liveVolState = undefined;
                    const pfSynth = pairedProxyHandler(handler);
                    if (pfSynth) {
                        pfSynth.histSeedBar = handler.lastDailyBar;
                        pfSynth.proxySt = undefined;
                    }
                    handler.pendingReload = false;
                } else {
                    return;
                }
            }
            const lastDailyBar: LastDailyBar = handler.lastDailyBar;
            const resolution: string = handler.resolution;
            // Index volume/OI proxy (ADVA-847): an index's own spot tick carries
            // volume=0 and no OI, so when this bar handler is linked to a
            // nearest-future proxy channel we substitute the future's latest
            // cumulative volume + OI (kept by the 'volume-proxy' handler). For a
            // normal scrip proxyChannel is undefined → effective* == the tick's own
            // values, leaving existing behaviour byte-for-byte unchanged.
            let effectiveVolume = volume;
            let effectiveOi: any = oi;
            if (handler.proxyChannel) {
                const pf = pairedProxyHandler(handler);
                const p = pf && pf.proxyLatest;
                effectiveVolume = p ? p.cumVolume : 0;
                effectiveOi = p ? p.oi : undefined;
            }
            var nextDailyBarTime: number = 0
            // Keep the parsed `tradeTime` pristine for the 'volume-proxy' candle
            // bucket; bucket the bar against `barTradeTime` instead.
            var barTradeTime: number = tradeTime
            if (resolution == '1') {
                nextDailyBarTime = getNextMinBarTime((lastDailyBar !== null && lastDailyBar !== undefined) ? lastDailyBar.time : null);
            } else if (resolution == '1D') {
                barTradeTime = moment(tradeTime).startOf('day').toDate().getTime() + 19800000
                nextDailyBarTime = getNextDailyBarTime((lastDailyBar !== null && lastDailyBar !== undefined) ? lastDailyBar.time : null);

            } else if (resolution == '1W') {
                barTradeTime = moment(tradeTime).startOf('week').toDate().getTime() + 86400000 + 19800000 //week start from monday so adding 1 day more
                nextDailyBarTime = getNextWeeklyBarTime((lastDailyBar !== null && lastDailyBar !== undefined) ? lastDailyBar.time : null);
            }
            else if (resolution == '1M') {
                barTradeTime = moment(tradeTime).startOf('month').toDate().getTime() + 19800000
                nextDailyBarTime = getNextMonthBarTime((lastDailyBar !== null && lastDailyBar !== undefined) ? lastDailyBar.time : null);
            } else if (resolution == '1Y') {
                barTradeTime = moment(tradeTime).startOf('year').toDate().getTime() + 19800000
                nextDailyBarTime = getNextyearBarTime((lastDailyBar !== null && lastDailyBar !== undefined) ? lastDailyBar.time : null);
            } else if (resolution == '1S') {
                barTradeTime = moment(tradeTime).startOf('second').toDate().getTime()
                nextDailyBarTime = getNextSecBarTime((lastDailyBar !== null && lastDailyBar !== undefined) ? lastDailyBar.time : null);
                // ADVA-1100: after a tick gap, lastBar+1s lags the tick's real
                // second and NEVER catches up (getNextSecBarTime has no wall
                // clock, unlike getNextMinBarTime) — every later 1S bar lands
                // progressively in the past and TV aggregates 5s/15s/30s from
                // the wrong buckets. Re-sync the new bar to the tick's second.
                if (nextDailyBarTime && barTradeTime > nextDailyBarTime) {
                    nextDailyBarTime = barTradeTime;
                }
            } else if (/^\d+$/.test(resolution)) {
                // Generic N-minute bucketing. Since intraday_multipliers now
                // declares the server-native minute resolutions (see
                // udf-compatible-datafeed-base resolveSymbol), v32 subscribes
                // realtime bars at that SAME base resolution (e.g. '60'), which
                // never reached this switch before. The grid is anchored to the
                // last history/pushed bar — that bar carries the server's
                // session phase (e.g. NSE hourly buckets start at 09:15 IST) —
                // and advances in N-minute steps; on a tick gap we snap forward
                // ALONG the grid, so the phase survives (every declared N
                // divides the 1440-minute day, so it also survives overnight).
                const periodMs = parseInt(resolution, 10) * 60000;
                const lastT = (lastDailyBar !== null && lastDailyBar !== undefined) ? Number(lastDailyBar.time) : NaN;
                if (isNaN(lastT)) {
                    // No history anchor yet (subscribe raced the first fetch):
                    // best-effort minute anchor; the next history reseed
                    // (pendingReload / visibility refetch) re-aligns the grid.
                    nextDailyBarTime = moment(tradeTime).startOf('minute').toDate().getTime();
                } else {
                    nextDailyBarTime = lastT + periodMs;
                    if (barTradeTime >= nextDailyBarTime + periodMs) {
                        nextDailyBarTime = lastT + Math.floor((barTradeTime - lastT) / periodMs) * periodMs;
                    }
                }
            }
            let bar: LastDailyBar = {};
            if (barTradeTime >= nextDailyBarTime) {
                if (resolution == '1D') {
                    if (openPrice > 0 && highPrice > 0 && lowPrice > 0 && closePrice > 0 && tradePrice > 0) {
                        bar = {
                            time: nextDailyBarTime,
                            open: openPrice / divider,
                            high: highPrice / divider,
                            low: lowPrice / divider,
                            close: tradePrice / divider,
                            volume: effectiveVolume
                        };
                    } else {
                        bar = {
                            time: nextDailyBarTime,
                        }
                    }
                } else if (resolution == '1W' || resolution == '1M' || resolution == '1Y') {
                    // ADVA-1100: W/M/Y bars must be volumed like 1D — from the
                    // tick's cumulative day volume — NOT via the intraday
                    // subtraction below: history never attaches
                    // totalLastDayVolumeTillBar to W/M/Y bars (see
                    // history-provider), so that math counted from page load.
                    // A brand-new period has no history bar → seed is 0 →
                    // volume = today's cumulative (e.g. a weekly bar born on
                    // Monday holds exactly today's volume).
                    bar = {
                        time: nextDailyBarTime,
                        open: tradePrice / divider,
                        high: tradePrice / divider,
                        low: tradePrice / divider,
                        close: tradePrice / divider,
                        volume: periodBarVolume(resolution, { cumVolume: effectiveVolume, perCandle: 0 }, handler.histSeedBar, nextDailyBarTime),
                    };
                } else {
                    bar = {
                        time: nextDailyBarTime,
                        open: tradePrice / divider,
                        high: tradePrice / divider,
                        low: tradePrice / divider,
                        close: tradePrice / divider,
                        // totalLastDayVolumeTillBar is still written for
                        // backward compat (synthesize/caches read it) but no
                        // longer drives volume — see intradayBarVolume.
                        volume: intradayBarVolume(handler, nextDailyBarTime, effectiveVolume),
                        totalLastDayVolumeTillBar: effectiveVolume
                    };
                }
            } else {
                if (resolution == '1D') {
                    bar = {
                        ...lastDailyBar,
                        high: highPrice > 0 ? highPrice / divider : lastDailyBar.high,
                        low: lowPrice > 0 ? lowPrice / divider : lastDailyBar.low,
                        open: openPrice > 0 ? openPrice / divider : lastDailyBar.open,
                        close: tradePrice / divider,
                        volume: effectiveVolume
                    };
                } else if (resolution == '1W' || resolution == '1M' || resolution == '1Y') {
                    // ADVA-1100: forming W/M/Y bar = history-served prior days of
                    // the period + today's cumulative (see periodBarVolume). The
                    // old subtraction ran against an undefined seed here → NaN.
                    bar = {
                        ...lastDailyBar,
                        high: Math.max(Number(lastDailyBar ? lastDailyBar.high : 0), tradePrice / divider),
                        low: Math.min(Number(lastDailyBar ? lastDailyBar.low : 0), tradePrice / divider),
                        close: tradePrice / divider,
                        volume: periodBarVolume(resolution, { cumVolume: effectiveVolume, perCandle: 0 }, handler.histSeedBar, lastDailyBar.time),
                    };
                } else {
                    bar = {
                        ...lastDailyBar,
                        high: Math.max(Number(lastDailyBar ? lastDailyBar.high : 0), tradePrice / divider),
                        low: Math.min(Number(lastDailyBar ? lastDailyBar.low : 0), tradePrice / divider),
                        close: tradePrice / divider,
                        volume: intradayBarVolume(handler, Number(lastDailyBar.time), effectiveVolume),
                    };
                }
            }
            if (bar.volume && bar.volume < 0) {
                bar.volume = 0
                // console.log("negative vol", volume,lastDailyBar.totalLastDayVolumeTillBar )
            }
            // Index volume/OI proxy: the index bar's TIME + OHLC come from the index
            // spot tick (built above); its VOLUME comes from the future stream
            // tracked in the uid-paired 'volume-proxy' handler — per resolution
            // class via periodBarVolume (ADVA-1100): 1D = the future's cumulative
            // day volume (as on the future's own chart), W/M/Y = history-served
            // prior days + today's cumulative, intraday = perCandle
            // (history-seeded, '1S' bucket-gated). OI carried likewise. No-op for
            // non-proxy scrips.
            if (handler.proxyChannel) {
                const pf = pairedProxyHandler(handler);
                const pl = pf && pf.proxyLatest;
                bar.volume = pl ? periodBarVolume(resolution, pl, handler.histSeedBar, bar.time) : 0;
                bar.oi = effectiveOi;
            }
            // v32 parity clamp: v26 silently tolerated the out-of-order pushes our
            // reset/backfill race produces (parked issue — see tv-upgrade/09 status
            // board); v32 validates each bar, logs "time order violation", and the
            // churn degrades seconds-resolution loading. Drop strictly-backwards
            // STREAM bars only (history untouched; equal times pass, they're
            // updates). Counter: window.__tvDroppedBars. A/B: window.__tvClampOff.
            const _prevPushed = (handler as any)._lastPushedBarTime;
            // Floor = newest of (last pushed stream bar, last HISTORY bar for
            // this symbol+resolution). The history side closes the reseed race:
            // v32 refetches history on every visibility resume, and a late tick
            // pushed behind that history's tail is exactly the "time order
            // violation" TV logs (repro: sparse MCX symbol, 5S, live edge).
            // Key mirrors the datafeed's write: full_name when present, else the
            // plain symbol (handler.fullName is "" when full_name was absent —
            // e.g. MCX contracts — so fall back to handler.symbol).
            const _histTail = HistoryTailCache.get(`${handler.fullName || handler.symbol}|${handler.resolution}`);
            const _floor = Math.max(
                typeof _prevPushed === 'number' ? _prevPushed : -Infinity,
                typeof _histTail === 'number' ? _histTail : -Infinity);
            const _isBackwards = bar && typeof bar.time === 'number'
                && isFinite(_floor) && (bar.time as number) < _floor;
            if (_isBackwards && !(window as any).__tvClampOff) {
                const w = window as any;
                w.__tvDroppedBars = (w.__tvDroppedBars || 0) + 1;
                if (w.__tvDroppedBars <= 3 || w.__tvDiag) {
                    console.warn('[TV32] dropped backwards stream bar', {
                        res: handler.resolution,
                        sym: handler.fullName,
                        prev: new Date(_floor).toISOString(),
                        next: new Date(bar.time as number).toISOString(),
                        totalDropped: w.__tvDroppedBars,
                    });
                }
            } else {
                if (bar && typeof bar.time === 'number') {
                    (handler as any)._lastPushedBarTime = bar.time;
                }
                handler.handler.callback(bar)

                handler.lastDailyBar = bar;
            }
        } else if (handler.type == 'volume-proxy') {
            // CAS cross-segment mismatch: from 2026-08-03 the future (NSE_FO) runs
            // to 15:40 while its index (NSE_EQ) still ends at 15:30.
            // proxyCandleStart returns a whole-day bucket for 1D/1W/1M/1Y, so the
            // "same candle" guard below never fires there and the future's
            // post-index-close volume would inflate the index's daily bar for 10
            // minutes. handler.sessionSpec is the INDEX's session (see makeHandler).
            if (!passesSessionGuard(handler, tradeTime)) return;
            // Nearest-future tick feeding an index chart (ADVA-847). We compute the
            // future's volume WITHIN the current candle ("perCandle") directly from
            // its cumulative day volume, tracking the candle base ourselves —
            // exactly how the future's own chart derives volume. This is independent
            // of the index's stored history (enriched server-side, possibly zero
            // where the backend enrichment isn't running), so the index's live
            // volume matches the future's chart bar-for-bar.
            const bucket = proxyCandleStart(tradeTime, handler.resolution);
            let st = handler.proxySt;
            if (!st || st.bucket !== bucket) {
                let base = volume;
                // ADVA-1100: on the very FIRST tick after subscribe the candle is
                // usually already mid-flight, and history served its volume so far
                // (P2b minute / P4 seconds enrichment). Basing on this tick's
                // cumulative would restart the forming bar at 0. When the tick is
                // still inside the bar history handed us, back the base off by
                // that bar's volume so perCandle = history volume + live delta.
                if (!st && handler.histSeedBar && handler.histSeedBar.time === bucket && Number.isFinite(handler.histSeedBar.volume)) {
                    base = volume - Number(handler.histSeedBar.volume);
                    if (base < 0) base = 0;
                }
                // ADVA-1100: carry the base across buckets — previous bucket's
                // last cumulative — which conserves volume (sum of live bars =
                // the future's cumulative growth, no first-delta loss). Vital
                // for '1S' where a bucket often sees a single tick (liquid
                // futures tick ~once a second — first-tick re-basing zeroed
                // nearly every live 1S bar and the aggregated 5s/15s/30s
                // candles with them), and it keeps the index bar-for-bar equal
                // to the future's own chart (intradayBarVolume uses the same
                // carry). `volume >= lastCum` guards the overnight cum reset.
                else if (st && Number.isFinite(st.lastCum) && volume >= Number(st.lastCum)) {
                    base = Number(st.lastCum);
                }
                st = { bucket: bucket, base: base };
                handler.proxySt = st;
            }
            st.lastCum = volume;
            let perCandle = volume - st.base;
            if (perCandle < 0) perCandle = 0;
            handler.proxyLatest = { cumVolume: volume, perCandle: perCandle, bucket: bucket, oi: oi, oi_ch: oi_ch };
            // Safe re-emit: refresh ONLY the index's current bar (same candle), so
            // the volume pane advances between index spot ticks. We never create or
            // move a candle here — the index spot tick owns time + OHLC + rollover —
            // so if the future has already rolled into a new candle we skip and let
            // the next index tick build it (prevents a future tick painting the new
            // candle's volume onto the old bar).
            const idxItem = handler.indexChannel ? channelToSubscription.get(handler.indexChannel) : undefined;
            if (idxItem && idxItem.handlers) {
                idxItem.handlers.forEach((idxHandler: Handlers) => {
                    if (idxHandler.type !== 'bar') return;
                    if (idxHandler.id !== handler.id) return; // uid-matched index<->proxy pair
                    const lb = idxHandler.lastDailyBar;
                    if (!lb || lb.time !== bucket) return; // different candle → let the index tick roll it
                    const bar: LastDailyBar = { ...lb, volume: periodBarVolume(handler.resolution, { cumVolume: volume, perCandle: perCandle, bucket: bucket }, handler.histSeedBar, lb.time), oi: oi };
                    try {
                        idxHandler.handler.callback(bar);
                    } catch (e) {
                        console.info("volume-proxy re-emit failed", e);
                    }
                    idxHandler.lastDailyBar = bar;
                });
            }
        } else if (handler.type == 'single-quotes') {
            let quote = {
                s: 'success',
                n: handler.symbol,
                v: {
                    ch: (tradePrice - closePrice) / divider,
                    chp: ((tradePrice - closePrice) / closePrice * 100),
                    lp: tradePrice / divider,
                    open_price: openPrice / divider,
                    high_price: highPrice / divider,
                    low_price: lowPrice / divider,
                    prev_close_price: closePrice / divider,
                    volume: volume,
                    dpr: dpr,
                    ask: sell_price,
                    ask_qty: sell_qty,
                    bid: buy_price,
                    bid_qty: buy_qty,
                    average_trade_price: ((highPrice + lowPrice) / 2) / (divider)
                }

            }
            let mapper = singleQuoteMap.get(handler.id)
            if (mapper) {
                let all = true
                type MapperValue = {
                    quote: {
                        n: string;
                        v: Subscribed;
                    };
                };
                type Mapper = {
                    [key: string]: MapperValue;
                };
                let callbackResp = []
                mapper[market_segment + "-" + token].quote = quote
                for (const [key, value] of Object.entries(mapper)) {
                    const typedValue = value as MapperValue;
                    if (Object.keys(typedValue.quote).length == 0) {
                        all = false
                    } else {
                        callbackResp.push({
                            "s": "ok",
                            "n": typedValue.quote.n,
                            "v": {
                                "ch": typedValue.quote.v.ch,
                                "chp": typedValue.quote.v.chp,
                                "short_name": typedValue.quote.n.split(":")[1],
                                "exchange": typedValue.quote.n.split(":")[0],
                                "description": typedValue.quote.n,
                                "lp": typedValue.quote.v.lp,
                                "ask": typedValue.quote.v.ask || 0,
                                "bid": typedValue.quote.v.bid || 0,
                                "open_price": typedValue.quote.v.open_price,
                                "high_price": typedValue.quote.v.high_price,
                                "low_price": typedValue.quote.v.low_price,
                                "prev_close_price": typedValue.quote.v.prev_close_price,
                                "volume": typedValue.quote.v.volume,
                                "dpr": typedValue.quote.v.dpr
                            }
                        })
                    }
                }
                if (all) {
                    handler.handler.callback(callbackResp)
                    singleQuoteMap.delete(handler.handler.id)
                    unsubscribe(handler.handler.id)
                }
            }


        }
        else if (handler.type == 'depth') {
            depth['ch'] = (tradePrice - closePrice) / divider
            depth['chp'] = ((tradePrice - closePrice) / closePrice * 100)
            depth['openPrice'] = openPrice / divider
            depth['closePrice'] = closePrice / divider
            depth['highPrice'] = highPrice / divider
            depth['lowPrice'] = lowPrice / divider
            depth['volume'] = volume
            depth['lp'] = tradePrice / divider
            depth['totalBuyQty'] = totalBuyQty
            depth['totalSellQty'] = totalSellQty
            depth['LTQ'] = lastTradeQuantity
            handler.handler.callback(depth)
        }

    });
}

function getNextDailyBarTime(barTime: number | null | undefined) {
    if (barTime == null) {
        var new_date = moment().startOf('day').toDate()
        return new_date.getTime() + 19800000
    }
    const date = new Date(barTime);
    var new_date = moment(date).add('1', 'd').startOf('day').toDate()
    return new_date.getTime() + 19800000;
}
function getNextWeeklyBarTime(barTime: number | null | undefined) {
    if (barTime == null) {
        var new_date = moment().startOf('day').toDate();
        return new_date.getTime() + 19800000;
    }
    const date = new Date(barTime);
    var new_date = moment(date).add('1', 'W').startOf('isoWeek').toDate(); // ISO week starts on Monday
    return new_date.getTime() + 19800000;
}
function getNextMonthBarTime(barTime: number | null | undefined) {
    if (barTime == null) {
        var new_date = moment().startOf('day').toDate()
        return new_date.getTime() + 19800000
    }
    const date = new Date(barTime);
    var new_date = moment(date).add('1', 'M').startOf('month').toDate()
    return new_date.getTime() + 19800000;
}

function getNextyearBarTime(barTime: number | null | undefined) {
    if (barTime == null) {
        var new_date = moment().startOf('day').toDate()
        return new_date.getTime() + 19800000
    }
    const date = new Date(barTime);
    var new_date = moment(date).add('1', 'Y').startOf('year').toDate()
    return new_date.getTime() + 19800000;
}

function getNextSecBarTime(barTime: number | null | undefined) {
    if (barTime == null) {
        var new_date = moment().startOf('day').toDate()
        return new_date.getTime() + 19800000
    }
    const date = new Date(barTime);
    var new_date = moment(date).add('1', 's').startOf('second').toDate()
    return new_date.getTime();
}
function getNextMinBarTime(barTime: number | null | undefined) {
    if (barTime == null) {
        var new_date = moment().startOf('day').toDate()
        return new_date.getTime() + 19800000
    }
    const date = new Date(barTime);
    const curTime = moment();
    var duration = moment.duration(curTime.diff(date)).asMinutes();
    if (duration > 1) {
        return curTime.startOf('minute').toDate().getTime()
    } else {
        var new_date = moment(date).add('1', 'm').startOf('minute').toDate()
        return new_date.getTime();
    }
}

// Start-of-candle (ms) for a trade time at the given resolution. Matches the
// `barTradeTime` / `time` the bar-build branches assign per resolution, so the
// volume proxy's candle buckets line up with the index bar handler's candles.
// Used only by the 'volume-proxy' handler to detect future-candle rollover.
function proxyCandleStart(tradeTime: number, resolution: string): number {
    if (resolution == '1S') {
        return moment(tradeTime).startOf('second').toDate().getTime();
    } else if (resolution == '1D') {
        return moment(tradeTime).startOf('day').toDate().getTime() + 19800000;
    } else if (resolution == '1W') {
        return moment(tradeTime).startOf('week').toDate().getTime() + 86400000 + 19800000;
    } else if (resolution == '1M') {
        return moment(tradeTime).startOf('month').toDate().getTime() + 19800000;
    } else if (resolution == '1Y') {
        return moment(tradeTime).startOf('year').toDate().getTime() + 19800000;
    }
    // '1' and any intraday minute resolution bucket to the start of the minute.
    return moment(tradeTime).startOf('minute').toDate().getTime();
}

// ADVA-1100: volume for a daily-class bar, by resolution — used by BOTH the
// index proxy (pl from the paired handler) and the generic bar build (pl built
// from the tick's own cumulative). 1D: the tick's cumulative day volume IS the
// day bar's volume (`/data/history` serves no forming day candle for any
// instrument, so there is nothing to seed). W/M/Y forming bars start from the
// history-served bar — prior days of the period; day history excludes today,
// so adding today's cumulative on top never double-counts. Intraday stays
// perCandle (base-tracked, seeded from history — see the handlers).
function periodBarVolume(resolution: string, pl: { cumVolume: number; perCandle: number; bucket?: number }, seedBar: LastDailyBar | undefined, barTime: number | undefined): number {
    if (resolution == '1D') {
        return pl.cumVolume;
    }
    if (resolution == '1W' || resolution == '1M' || resolution == '1Y') {
        const seed = (seedBar && seedBar.time === barTime && Number.isFinite(seedBar.volume)) ? Number(seedBar.volume) : 0;
        return seed + pl.cumVolume;
    }
    // 1S: only the future's CURRENT second's volume belongs on this bar. The
    // index ticks every second while the future doesn't — without this gate a
    // stale perCandle repeats onto every index second-bar built between future
    // ticks, and TV's 5s/15s/30s aggregation SUMS the duplicates. No fut trade
    // in this second ⇒ volume 0, matching the enriched history shape (index
    // seconds are 0 at non-fut-trade seconds). Minute buckets keep the ungated
    // behaviour: both legs trade every minute (staleness is a brief boundary
    // transient) and minute bar times are wall-clock derived
    // (getNextMinBarTime), which the fut-tick bucket may not equal.
    if (resolution == '1S' && pl.bucket !== undefined && pl.bucket !== barTime) {
        return 0;
    }
    return pl.perCandle;
}

// The volume-proxy handler paired with an index 'bar' handler: same uid, on
// the future channel the bar handler points at. Undefined for non-proxy bars.
function pairedProxyHandler(barHandler: Handlers): Handlers | undefined {
    const item = barHandler.proxyChannel ? channelToSubscription.get(barHandler.proxyChannel) : undefined;
    if (!item || !item.handlers) return undefined;
    return item.handlers.find((h: Handlers) => h.type === 'volume-proxy' && h.id === barHandler.id);
}

// ADVA-1100: live volume for an intraday bar of ANY symbol, tracked per handler
// from the tick's cumulative day volume. Replaces the old
// `cum − totalLastDayVolumeTillBar` subtraction, whose seed was only as good as
// the history response window — partial windows inflated the forming bar,
// stale/refetched seeds deflated it. Model mirrors the volume-proxy accounting:
// seed the forming bar from the history bar it continues, then carry the base
// across bars (previous bar's last cumulative) so live volume is conserved;
// `cum >= lastCum` guards the overnight cumulative reset.
function intradayBarVolume(handler: Handlers, barTime: number, cum: number): number {
    let st = handler.liveVolState;
    if (!st) {
        let base = cum;
        const seed = handler.histSeedBar;
        if (seed && seed.time === barTime && Number.isFinite(seed.volume)) {
            base = cum - Number(seed.volume);
            if (base < 0) base = 0;
        }
        st = handler.liveVolState = { barTime: barTime, base: base };
    } else if (st.barTime !== barTime) {
        st.barTime = barTime;
        st.base = (Number.isFinite(st.lastCum) && cum >= Number(st.lastCum)) ? Number(st.lastCum) : cum;
    }
    st.lastCum = cum;
    const v = cum - st.base;
    return v < 0 ? 0 : v;
}

function odinDateParse(rand: string) {
    let [dateStr, timeStr] = rand.split(" ")
    let final_time = "T" + timeStr[0] + timeStr[1] + ":" + timeStr[2] + timeStr[3] + ":" + timeStr[4] + timeStr[5] + "+05:30"
    return Date.parse(dateStr + final_time)
}

//Return odin request parameters for depth Subscription
function _getSubscribeDepthRequest(symbol: ResolveSymbolInfo) {
    var request = "63=FT3.0|64=127|65=80|"
    if (symbol.odin_market_segment_id === undefined) {
        if (symbol.market_segment_id !== undefined) {
            symbol.odin_market_segment_id = symbol.market_segment_id
        }
    }
    if (symbol.odin_token === undefined) {
        symbol.odin_token = symbol.token
    }
    return request + "1=" + symbol.odin_market_segment_id + "|7=" + symbol.odin_token + "|230=1"
}

//Return odin request parameters for depth Unsubscription
function _getUnsubscribeDepthRequest(symbol: ResolveSymbolInfo) {
    var request = "63=FT3.0|64=206|65=80|"
    return request + "1=" + symbol.odin_market_segment_id + "|7=" + symbol.odin_token + "|230=2"
}

//Return odin request parameters for touchLine Unsubscription
function _getUnsubscribeTouchRequest(symbol: ResolveSymbolInfo) {
    var request = "63=FT3.0|64=127|65=80|"
    return request + "1=" + symbol.odin_market_segment_id + "&7=" + symbol.odin_token + "|230=2"
}

//Return odin request parameters for touchLine Subscription
function _getSubscribeTouchRequest(symbol: ResolveSymbolInfo) {
    if (symbol.odin_market_segment_id === undefined) {
        if (symbol.market_segment_id !== undefined) {
            symbol.odin_market_segment_id = symbol.market_segment_id
        }
    }
    if (symbol.odin_token === undefined) {
        symbol.odin_token = String(symbol.token)
    }
    return "63=FT3.0|64=206|65=80|1=" + symbol.odin_market_segment_id + "$7=" + symbol.odin_token + "|" + "4=1000|230=1"
}

function _subscribeDepth(symbol: ResolveSymbolInfo, onRealtimeCallback: (a: Quotes[]) => void, subscribeUID: string) {
    var channelString = symbol.odin_market_segment_id + "-" + symbol.odin_token
    _setChannelMap(symbol, channelString, onRealtimeCallback, subscribeUID, 'depth')
    // SendMessage(_getSubscribeDepthRequest(symbol))
}

function _subscribeQuotes(symbols: ResolveSymbolInfo[], onRealtimeCallback: (a: Quotes[]) => void, subscribeUID: string, type: string) {
    symbols.forEach((symbol: ResolveSymbolInfo) => {
        let channelString = symbol.odin_market_segment_id + "-" + symbol.odin_token
        _setChannelMap(symbol, channelString, onRealtimeCallback, subscribeUID, type)
    })
    // SendMessage(_getTouchRequest(symbols))
}

function _subscribeBars(symbols: ResolveSymbolInfo[], onRealtimeCallback: (a: Quotes[]) => void, subscribeUID: string, lastDailyBar: LastDailyBar, resolution: string) {
    symbols.forEach((symbol: ResolveSymbolInfo) => {
        let channelString = symbol.odin_market_segment_id + "-" + symbol.odin_token

        // Index volume/OI proxy (ADVA-847): an index spot feed carries no volume/OI,
        // so when symbol_info advertises a nearest-future proxy we ALSO subscribe
        // that future's channel and merge its volume/OI onto the index bar (see the
        // 'bar' and 'volume-proxy' handlers in ProcessPacketString).
        let proxyChannel: string | null = null;
        if (symbol.has_volume_proxy && symbol.volume_source_token && symbol.volume_source_market_segment_id) {
            proxyChannel = symbol.volume_source_market_segment_id + "-" + symbol.volume_source_token;
        }

        _setChannelMap(symbol, channelString, onRealtimeCallback, subscribeUID, 'bar', resolution, lastDailyBar, { proxyChannel })

        if (proxyChannel) {
            _setChannelMap(symbol, proxyChannel, onRealtimeCallback, subscribeUID, 'volume-proxy', resolution, lastDailyBar, { indexChannel: channelString })
            // The future channel isn't in `symbols`, so the socket-subscribe loop in
            // subscribeOnOdinStream won't cover it — subscribe it (touchline) here.
            const proxySym: ResolveSymbolInfo = {
                odin_market_segment_id: Number(symbol.volume_source_market_segment_id),
                odin_token: String(symbol.volume_source_token),
            };
            const proxySubs = channelToSubscription.get(proxyChannel);
            if (proxySubs && !proxySubs.isSubscribed) {
                SendMessage(_getSubscribeTouchRequest(proxySym));
                proxySubs.isSubscribed = true;
                proxySubs.isTouchline = true;
                channelToSubscription.set(proxyChannel, proxySubs);
            }
        }
    })
    // SendMessage(_getTouchRequest(symbols))
}

function _subscribeSingleQuotes(symbols: ResolveSymbolInfo[], onRealtimeCallback: (a: Quotes[]) => void, subscribeUID: string) {
    symbols.forEach((symbol: ResolveSymbolInfo) => {
        let channelString = symbol.odin_market_segment_id + "-" + symbol.odin_token
        let existing = singleQuoteMap.get(subscribeUID)
        if (existing) {
            existing[symbol.odin_market_segment_id + "-" + symbol.odin_token] = { quote: {} }
        } else {
            singleQuoteMap.set(subscribeUID, new Object())
            singleQuoteMap.get(subscribeUID)[symbol.odin_market_segment_id + "-" + symbol.odin_token] = { quote: {}, symbol: symbol.symbol }
        }
        _setChannelMap(symbol, channelString, onRealtimeCallback, subscribeUID, 'single-quotes')
    })
    // SendMessage(_getTouchRequest(symbols))
}
//Map the other requirements like id,resolution , type etc. if subscribeUID doesn't exist
function _setChannelMap(symbol: ResolveSymbolInfo, channelString: string, onRealtimeCallback: (a: Quotes[]) => void, subscribeUID: string, type: string, resolution = '', lastDailyBar: LastDailyBar = {}, opts: { proxyChannel?: string | null; indexChannel?: string } = {}) {
    let subscriptionItem = channelToSubscription.get(channelString);
    let handler = {
        id: subscribeUID,
        callback: onRealtimeCallback,
    };
    // proxyChannel: set on an index 'bar' handler → which future channel to read
    // volume/OI from. indexChannel: set on a 'volume-proxy' handler → which index
    // channel to feed. Both undefined for ordinary subscriptions (ADVA-847).
    const proxyChannel = (opts && opts.proxyChannel) || undefined;
    const indexChannel = (opts && opts.indexChannel) || undefined;
    // histSeedBar: frozen copy of the subscribe-time last history bar. The 'bar'
    // handler mutates lastDailyBar on every tick, but the proxy volume logic
    // (ADVA-1100) must keep seeing what HISTORY said the forming bar held.
    // sessionSpec is compiled from THIS symbol's session — and for a 'volume-proxy'
    // handler `symbol` is the INDEX (we subscribe the future's channel on the
    // index's behalf), so the proxy is gated by the index's session, not the
    // future's. That is why the guard is per-handler and not at the top of
    // ProcessPacketString: one channel can carry handlers with two different,
    // both-correct, sessions.
    const makeHandler = (): Handlers => ({ sessionSpec: specFor(symbol), handler: handler, type: type, resolution: resolution, lastDailyBar: lastDailyBar, histSeedBar: lastDailyBar, symbol: String(symbol.symbol), fullName: String(symbol.full_name || ""), id: subscribeUID, proxyChannel: proxyChannel, indexChannel: indexChannel });
    if (subscriptionItem) {
        // Match on id AND type: the same guid can hold a 'bar' on the index channel
        // and a 'volume-proxy' on the future channel — distinct rows.
        var index = subscriptionItem.handlers.findIndex((object: Handlers) => {
            return object.id == subscribeUID && object.type == type
        })
        if (index == -1) {
            subscriptionItem.handlers.push(makeHandler());
        } else {
            subscriptionItem.handlers[index] = makeHandler()
        }
    } else {
        subscriptionItem = {
            handlers: [makeHandler()],
            isSubscribed: false,
            isMarketDepth: false,
            isTouchline: false,
            lastPacket: ""
        };
    }
    channelToSubscription.set(channelString, subscriptionItem as OdinMessagePacket);
    // guid -> [channels]. A bar sub with a volume proxy spans two channels, so
    // track an array and append (deduped) rather than overwrite.
    let chans = guidToSubscription.get(subscribeUID);
    if (!Array.isArray(chans)) {
        chans = [];
        guidToSubscription.set(subscribeUID, chans);
    }
    if (chans.indexOf(channelString) === -1) {
        chans.push(channelString);
    }
}

//Change the market depth data in a particular format from Odin's response
function marketDepthProcessMessage(message: string) {
    var scrip = message.split("|");
    let quantity_a: string[] = []
    let price_a: string[] = []
    let order_a: string[] = []
    let depth: MarketDepth = {
        snapshot: true,
        asks: [],
        bids: []
    }
    let data_map = new Map()

    for (var i = 0, len = scrip.length; i < len; i++) {
        var data = scrip[i];
        var pos = data.indexOf('=');
        if (pos >= 0) {
            var key = data.substring(0, pos);
            var value = data.substring(pos + 1, scrip[i].length);
            if (key == '11') {
                var buySell = data.substring(pos + 1, scrip[i].length);
                //BuySell==1 means buy market depth 
                if (buySell == '1') {
                    for (var j = 0, len2 = 5; j < len2; j++) {
                        let marketDepthData = scrip[i + j + 1].split("$")
                        marketDepthData.map((d: string, index: number) => {
                            if (index == 0) {
                                quantity_a = d.split("=")
                            } else if (index == 1) {
                                price_a = d.split("=")
                            } else if (index == 2) {
                                order_a = d.split("=")
                            }
                        })
                        depth.bids?.push({
                            price: price_a.length > 1 ? parseFloat(price_a[1]) : 0,
                            volume: quantity_a.length > 1 ? parseInt(quantity_a[1]) : 0,
                            orders: order_a.length > 1 ? parseInt(order_a[1]) : 0
                        })
                    }
                } else {
                    for (var j = 0, len2 = 5; j < len2; j++) {
                        let marketDepthData = scrip[i + j + 1].split("$")
                        marketDepthData.map((d: string, index: number) => {
                            if (index == 0) {
                                quantity_a = d.split("=")
                            } else if (index == 1) {
                                price_a = d.split("=")
                            } else if (index == 2) {
                                order_a = d.split("=")
                            }
                        })
                        depth.asks?.push({
                            price: price_a.length > 1 ? parseFloat(price_a[1]) : 0,
                            volume: quantity_a.length > 1 ? parseInt(quantity_a[1]) : 0,
                            orders: order_a.length > 1 ? parseInt(order_a[1]) : 0
                        })
                    }

                }

            } else {
                data_map.set(key, value)
            }
            // processMarketDepthKeyValuePair(scripDetailModel, key, value);
        }
    }
    depth.asks?.sort((a: Bids, b: Bids) => (a.price > b.price) ? 1 : ((b.price > a.price) ? -1 : 0))
    depth.bids?.sort((a: Bids, b: Bids) => (a.price > b.price) ? 1 : ((b.price > a.price) ? -1 : 0))
    data_map.set("depth", depth)
    return data_map
}